import React, { useState } from 'react';
import { Nav, Footer, DisclaimerBar } from '../components/Chrome';
import { useI18n } from '../i18n';
import { api } from '../api';
import { useRecaptcha } from '../hooks/useRecaptcha';

export default function Contact() {
  const { t, lang } = useI18n();
  const executeRecaptcha = useRecaptcha();
  const [form, setForm] = useState({ name: '', email: '', company: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErr('');
    try {
      const recaptcha_token = await executeRecaptcha('contact_submit');
      await api.post('/contact', { ...form, recaptcha_token });
      setStatus('success');
    } catch (e) {
      setStatus('error');
      setErr(e?.response?.data?.detail || 'Error');
    }
  };

  const inputCls = "bg-obsidian border hairline px-3 py-2.5 mono text-sm text-white placeholder-white/30 focus:border-gold outline-none";

  return (
    <div>
      <Nav />
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="mono text-[10px] uppercase tracking-widest2 text-gold mb-4">
            {lang === 'es' ? 'CONTACTO' : 'CONTACT'}
          </div>
          <h1 className="serif text-5xl md:text-6xl leading-tight">
            {lang === 'es' ? 'Análisis personalizados de propiedad.' : 'Custom property intelligence.'}
          </h1>
          <p className="mt-6 text-white/70 leading-relaxed">
            {lang === 'es'
              ? 'Para portfolios, family offices, private lenders y brokers boutique: escríbenos para diseñar un desk de inteligencia a la medida.'
              : 'For portfolios, family offices, private lenders, and boutique brokers: reach out to design a bespoke intelligence desk.'}
          </p>
          <div className="mt-8 border hairline bg-surface1 p-6">
            <div className="mono text-[10px] uppercase tracking-widest2 text-white/40">Direct line</div>
            <a href="mailto:ceo@pheigroup.com" className="serif text-2xl text-gold mt-2 block hover:text-white" data-testid="contact-email-direct">ceo@pheigroup.com</a>
            <div className="mt-4 mono text-[10px] uppercase tracking-widest2 text-white/40">Company</div>
            <a href="https://www.pheigroup.com" target="_blank" rel="noreferrer" className="serif text-xl text-white mt-1 block hover:text-gold" data-testid="contact-company-link">www.pheigroup.com</a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="border hairline bg-surface1">
            <div className="border-b hairline px-6 py-3 mono text-[11px] uppercase tracking-widest2 text-white/80">
              {lang === 'es' ? 'Solicitud de análisis personalizado' : 'Custom analysis request'}
            </div>
            {status === 'success' ? (
              <div className="p-10 text-center">
                <div className="mono text-[10px] uppercase tracking-widest2 text-gold">RECEIVED</div>
                <div className="serif text-3xl mt-4">
                  {lang === 'es' ? 'Recibimos tu solicitud.' : 'We received your request.'}
                </div>
                <p className="mt-4 text-white/60">
                  {lang === 'es' ? 'Un miembro del equipo responderá en 1 día hábil.' : 'A team member will respond within 1 business day.'}
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6 space-y-3" data-testid="contact-form">
                <input required placeholder={t('intake.field.name')} className={inputCls + ' w-full'} value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="contact-name" />
                <input required type="email" placeholder={t('intake.field.email')} className={inputCls + ' w-full'} value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="contact-email" />
                <input placeholder={lang === 'es' ? 'Empresa (opcional)' : 'Company (optional)'} className={inputCls + ' w-full'} value={form.company} onChange={e=>setForm({...form, company:e.target.value})} data-testid="contact-company" />
                <input required placeholder={lang === 'es' ? 'Asunto' : 'Subject'} className={inputCls + ' w-full'} value={form.subject} onChange={e=>setForm({...form, subject:e.target.value})} data-testid="contact-subject" />
                <textarea required rows={6} placeholder={lang === 'es' ? '¿Qué necesitas analizar? Portfolio, territorio, deal específico...' : 'What do you need analyzed? Portfolio, territory, specific deal...'} className={inputCls + ' w-full'} value={form.message} onChange={e=>setForm({...form, message:e.target.value})} data-testid="contact-message" />
                {err && <div className="text-red-400 mono text-xs">{err}</div>}
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full py-3 bg-gold text-obsidian mono text-[11px] uppercase tracking-widest2 hover:bg-white disabled:opacity-50"
                  style={{transitionProperty: 'background-color'}}
                  data-testid="contact-submit"
                >
                  {status === 'sending' ? (lang === 'es' ? 'Enviando…' : 'Sending…') : (lang === 'es' ? 'Enviar Solicitud' : 'Send Request')} →
                </button>
                <div className="mono text-[10px] uppercase tracking-widest2 text-white/40 pt-2">
                  {lang === 'es' ? 'Protegido por reCAPTCHA · ' : 'Protected by reCAPTCHA · '}
                  <a href="https://policies.google.com/privacy" className="hover:text-gold" target="_blank" rel="noreferrer">Privacy</a> · <a href="https://policies.google.com/terms" className="hover:text-gold" target="_blank" rel="noreferrer">Terms</a>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      <DisclaimerBar />
      <Footer />
    </div>
  );
}
